﻿import { type DragEvent, type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { SearchInput, FilterBar, StatusBadge, PriorityBadge, Drawer, useToast } from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import { useCases } from '@/hooks/useCases';
import { useLegalProducts } from '@/hooks/useLegalProducts';
import { useLeads } from '@/hooks/useLeads';
import { type CreateTaskInput, type Task, useTasks } from '@/hooks/useTasks';

type TaskFormState = {
  title: string;
  description: string;
  status: string;
  priority: string;
  taskType: string;
  clientId: string;
  caseId: string;
  leadId: string;
  legalProductId: string;
  responsibleUser: string;
  dueDate: string;
  completedAt: string;
  notes: string;
};

const taskStatusFilterOptions = [
  { label: 'A fazer', value: 'pending' },
  { label: 'Em andamento', value: 'in_progress' },
  { label: 'Concluída', value: 'completed' },
  { label: 'Cancelada', value: 'cancelled' },
  { label: 'Arquivado', value: 'archived' },
];

const taskPriorityFilterOptions = [
  { label: 'Baixa', value: 'low' },
  { label: 'Normal', value: 'normal' },
  { label: 'Alta', value: 'high' },
  { label: 'Urgente', value: 'urgent' },
];

const taskTypeOptions = [
  { label: 'Geral', value: 'general' },
  { label: 'Prazo', value: 'deadline' },
  { label: 'Audiência', value: 'hearing' },
  { label: 'Documento', value: 'document' },
  { label: 'Retorno', value: 'follow_up' },
  { label: 'Financeiro', value: 'financial' },
  { label: 'Contato com cliente', value: 'client_contact' },
  { label: 'Interno', value: 'internal' },
];

const taskStatusLabelMap: Record<string, string> = {
  pending: 'A fazer',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  archived: 'Arquivado',
};

const taskPriorityLabelMap: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

const taskTypeLabelMap: Record<string, string> = {
  general: 'Geral',
  deadline: 'Prazo',
  hearing: 'Audiência',
  document: 'Documento',
  follow_up: 'Retorno',
  financial: 'Financeiro',
  client_contact: 'Contato com cliente',
  internal: 'Interno',
};

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const compactText = (...values: Array<string | null | undefined>) =>
  values.filter((value): value is string => Boolean(value && value.trim())).join(' · ');

const emptyTaskForm = (): TaskFormState => ({
  title: '',
  description: '',
  status: 'pending',
  priority: 'normal',
  taskType: 'general',
  clientId: '',
  caseId: '',
  leadId: '',
  legalProductId: '',
  responsibleUser: 'Vinícius Meschick',
  dueDate: '',
  completedAt: '',
  notes: '',
});

const taskToFormState = (task: Task): TaskFormState => ({
  title: task.title,
  description: task.description ?? '',
  status: task.status,
  priority: task.priority,
  taskType: task.taskType,
  clientId: task.clientId ?? '',
  caseId: task.caseId ?? '',
  leadId: task.leadId ?? '',
  legalProductId: task.legalProductId ?? '',
  responsibleUser: task.responsibleUser ?? 'Vinícius Meschick',
  dueDate: task.dueDate ?? '',
  completedAt: task.completedAt ?? '',
  notes: task.notes ?? '',
});

const buildCreateTaskInput = (form: TaskFormState): CreateTaskInput => ({
  title: form.title.trim(),
  description: toNullableText(form.description),
  status: form.status,
  priority: form.priority,
  taskType: form.taskType,
  clientId: toNullableText(form.clientId),
  caseId: toNullableText(form.caseId),
  leadId: toNullableText(form.leadId),
  legalProductId: toNullableText(form.legalProductId),
  responsibleUser: toNullableText(form.responsibleUser),
  dueDate: toNullableText(form.dueDate),
  completedAt: toNullableText(form.completedAt),
  notes: toNullableText(form.notes),
});

const buildTaskPayloadFromTask = (task: Task, statusOverride?: string): CreateTaskInput => ({
  title: task.title.trim(),
  description: toNullableText(task.description ?? ''),
  status: statusOverride ?? task.status,
  priority: task.priority,
  taskType: task.taskType,
  clientId: toNullableText(task.clientId ?? ''),
  caseId: toNullableText(task.caseId ?? ''),
  leadId: toNullableText(task.leadId ?? ''),
  legalProductId: toNullableText(task.legalProductId ?? ''),
  responsibleUser: toNullableText(task.responsibleUser ?? ''),
  dueDate: toNullableText(task.dueDate ?? ''),
  completedAt: toNullableText(task.completedAt ?? ''),
  notes: toNullableText(task.notes ?? ''),
});

const getTaskStatusLabel = (status: string) => taskStatusLabelMap[status] ?? status;
const getTaskPriorityLabel = (priority: string) => taskPriorityLabelMap[priority] ?? priority;
const getTaskTypeLabel = (taskType: string) => taskTypeLabelMap[taskType] ?? taskType;

const TasksPage: FC = () => {
  const { addToast } = useToast();
  const { clients, clientsLoading } = useAppContext();
  const { cases, isLoadingCases } = useCases();
  const { leads, isLoadingLeads } = useLeads();
  const { legalProducts, isLoadingLegalProducts } = useLegalProducts();
  const { tasks, isLoadingTasks, tasksError, loadTasks, createTask, updateTask, completeTask, deleteTask } = useTasks();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [showNewTask, setShowNewTask] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState('');
  const [archivingTaskId, setArchivingTaskId] = useState('');
  const [draggingTaskId, setDraggingTaskId] = useState('');
  const [dragOverStatus, setDragOverStatus] = useState('');
  const [newTaskForm, setNewTaskForm] = useState<TaskFormState>(emptyTaskForm());

  const clientOptions = useMemo(
    () =>
      clients
        .map((client) => ({
          value: client.id,
          label: client.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [clients]
  );

  const caseOptions = useMemo(
    () =>
      cases
        .map((caseItem) => ({
          value: caseItem.id,
          label: compactText(caseItem.title, caseItem.clientName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [cases]
  );

  const leadOptions = useMemo(
    () =>
      leads
        .map((lead) => ({
          value: lead.id,
          label: compactText(lead.name, lead.legalProductName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [leads]
  );

  const legalProductOptions = useMemo(
    () =>
      legalProducts
        .filter((product) => product.isActive)
        .map((product) => ({
          value: product.id,
          label: compactText(product.name, product.category),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [legalProducts]
  );

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();

    return tasks.filter((task) => {
      if (search) {
        const haystack = [
          task.title,
          task.description,
          task.clientName,
          task.caseTitle,
          task.leadName,
          task.legalProductName,
          task.responsibleUser,
          task.notes,
          task.taskType,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

        if (!haystack) {
          return false;
        }
      }

      if (statusFilter && task.status !== statusFilter) {
        return false;
      }

      if (priorityFilter && task.priority !== priorityFilter) {
        return false;
      }

      return true;
    });
  }, [priorityFilter, search, statusFilter, tasks]);

  const visibleTasks = useMemo(
    () => (statusFilter ? filteredTasks : filteredTasks.filter((task) => task.status !== 'archived')),
    [filteredTasks, statusFilter]
  );

  const kanbanStatusOptions = useMemo(
    () => taskStatusFilterOptions.filter((option) => option.value !== 'archived' || statusFilter === 'archived'),
    [statusFilter]
  );

  const kanbanColumns = useMemo(
    () =>
      kanbanStatusOptions.reduce<Record<string, Task[]>>((acc, option) => {
        acc[option.value] = visibleTasks.filter((task) => task.status === option.value);
        return acc;
      }, {}),
    [kanbanStatusOptions, visibleTasks]
  );

  const completedTasksCount = useMemo(() => tasks.filter((task) => task.status === 'completed').length, [tasks]);

  const openNewTaskDrawer = () => {
    setSelectedTask(null);
    setEditingTask(null);
    setNewTaskForm(emptyTaskForm());
    setShowNewTask(true);
  };

  const openEditTask = (task: Task) => {
    setSelectedTask(null);
    setEditingTask(task);
    setNewTaskForm(taskToFormState(task));
    setShowNewTask(true);
  };

  const closeNewTaskDrawer = () => {
    if (isSavingTask) {
      return;
    }

    setShowNewTask(false);
    setEditingTask(null);
    setNewTaskForm(emptyTaskForm());
  };

  const handleSaveNewTask = async () => {
    if (isSavingTask) {
      return;
    }

    if (!newTaskForm.title.trim()) {
      addToast('Preencha o título da tarefa.', 'warning');
      return;
    }

    setIsSavingTask(true);

    try {
      const payload = buildCreateTaskInput(newTaskForm);

      if (editingTask) {
        const updated = await updateTask(editingTask.id, payload);
        addToast('Tarefa atualizada com sucesso.', 'success');

        if (selectedTask?.id === editingTask.id) {
          setSelectedTask(updated);
        }
      } else {
        await createTask(payload);
        addToast('Tarefa criada com sucesso.', 'success');
      }

      setShowNewTask(false);
      setEditingTask(null);
      setNewTaskForm(emptyTaskForm());
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível salvar a tarefa.', 'error');
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleCompleteTask = async (task: Task) => {
    if (isSavingTask || completingTaskId || archivingTaskId) {
      return;
    }

    const confirmed = window.confirm('Tem certeza que deseja concluir esta tarefa?');
    if (!confirmed) {
      return;
    }

    setCompletingTaskId(task.id);

    try {
      const completed = await completeTask(task.id);
      addToast('Tarefa concluída com sucesso.', 'success');

      if (selectedTask?.id === task.id) {
        setSelectedTask(completed);
      }

      if (editingTask?.id === task.id) {
        setEditingTask(completed);
        setNewTaskForm(taskToFormState(completed));
      }
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível concluir a tarefa.', 'error');
    } finally {
      setCompletingTaskId('');
    }
  };

  const handleArchiveTask = async (task: Task) => {
    if (isSavingTask || completingTaskId || archivingTaskId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar esta tarefa? Esta ação removerá a tarefa da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setArchivingTaskId(task.id);

    try {
      await deleteTask(task.id);
      addToast('Tarefa arquivada com sucesso.', 'success');

      if (selectedTask?.id === task.id) {
        setSelectedTask(null);
      }

      if (editingTask?.id === task.id) {
        setShowNewTask(false);
        setEditingTask(null);
        setNewTaskForm(emptyTaskForm());
      }
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível arquivar a tarefa.', 'error');
    } finally {
      setArchivingTaskId('');
    }
  };

  const handleMoveTaskToStatus = async (task: Task, nextStatus: string) => {
    const trimmedStatus = nextStatus.trim();

    if (
      !trimmedStatus ||
      trimmedStatus === task.status ||
      isSavingTask ||
      completingTaskId ||
      archivingTaskId
    ) {
      return;
    }

    if (!taskStatusFilterOptions.some((option) => option.value === trimmedStatus)) {
      addToast('Status inválido para movimentação.', 'warning');
      return;
    }

    try {
      const updated = await updateTask(task.id, buildTaskPayloadFromTask(task, trimmedStatus));
      addToast(`Tarefa movida para ${getTaskStatusLabel(trimmedStatus)}.`, 'success');

      if (selectedTask?.id === task.id) {
        setSelectedTask(updated);
      }

      if (editingTask?.id === task.id) {
        setEditingTask(updated);
        setNewTaskForm(taskToFormState(updated));
      }
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível mover a tarefa.', 'error');
      await loadTasks();
    }
  };

  const handleTaskDragStart = (task: Task, event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', task.id);
    setDraggingTaskId(task.id);
  };

  const handleTaskDragEnd = () => {
    setDraggingTaskId('');
    setDragOverStatus('');
  };

  const handleColumnDragOver = (status: string, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (dragOverStatus !== status) {
      setDragOverStatus(status);
    }
  };

  const handleColumnDrop = async (status: string, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const taskId = event.dataTransfer.getData('text/plain');
    const task = tasks.find((item) => item.id === taskId);

    setDragOverStatus('');
    setDraggingTaskId('');

    if (!task) {
      return;
    }

    await handleMoveTaskToStatus(task, status);
  };

  const handleRetryTasks = () => {
    void loadTasks();
  };

  const saveButtonClassName = `px-3 py-1.5 rounded-lg text-xs font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap ${
    isSavingTask ? 'bg-primary-300' : 'bg-primary-500 hover:bg-primary-600'
  }`;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Tarefas</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              {isLoadingTasks
                ? 'Carregando tarefas...'
                : `${visibleTasks.length} tarefas · ${completedTasksCount} concluídas · ${tasksError ? 'Dados locais' : 'Controle operacional'}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-background-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'list' ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-500'
                }`}
              >
                <i className="ri-list-check mr-1"></i>Lista
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'kanban' ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-500'
                }`}
              >
                <i className="ri-layout-column-line mr-1"></i>Kanban
              </button>
            </div>
            <button
              onClick={openNewTaskDrawer}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> Nova Tarefa
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar tarefa..." className="sm:w-64" />
          <FilterBar
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: taskStatusFilterOptions,
                value: statusFilter,
                onChange: setStatusFilter,
              },
              {
                key: 'priority',
                label: 'Prioridade',
                options: taskPriorityFilterOptions,
                value: priorityFilter,
                onChange: setPriorityFilter,
              },
            ]}
          />
        </div>

        {tasksError && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span>{tasksError}</span>
            <button
              onClick={handleRetryTasks}
              className="px-2.5 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {viewMode === 'list' ? (
          <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Título</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">Cliente</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Responsável</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Prazo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Prioridade</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTasks && visibleTasks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-foreground-500">
                        Carregando tarefas...
                      </td>
                    </tr>
                  ) : visibleTasks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-foreground-500">
                        Nenhuma tarefa encontrada.
                      </td>
                    </tr>
                  ) : (
                    visibleTasks.map((task) => (
                      <tr
                        key={task.id}
                        className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedTask(task)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-foreground-800">{task.title}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{compactText(task.caseTitle, task.leadName, task.legalProductName) || '—'}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">{task.clientName ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">{task.responsibleUser ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{task.dueDate ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <PriorityBadge priority={getTaskPriorityLabel(task.priority)} size="sm" />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={getTaskStatusLabel(task.status)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditTask(task);
                              }}
                              disabled={isSavingTask || Boolean(completingTaskId) || Boolean(archivingTaskId)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Editar
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleCompleteTask(task);
                              }}
                              disabled={isSavingTask || completingTaskId === task.id || Boolean(archivingTaskId) || task.status === 'completed'}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {completingTaskId === task.id ? 'Concluindo...' : 'Concluir'}
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleArchiveTask(task);
                              }}
                              disabled={isSavingTask || Boolean(completingTaskId) || archivingTaskId === task.id}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {archivingTaskId === task.id ? 'Arquivando...' : 'Arquivar'}
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedTask(task);
                              }}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-right-s-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex gap-3 min-w-max pb-2">
              {taskStatusFilterOptions.map((option) => (
                <div
                  key={option.value}
                  className={`w-64 flex-shrink-0 rounded-xl transition-colors ${
                    dragOverStatus === option.value ? 'bg-primary-50/60' : ''
                  }`}
                  onDragOver={(event) => handleColumnDragOver(option.value, event)}
                  onDrop={(event) => void handleColumnDrop(option.value, event)}
                >
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">{option.label}</span>
                    <span className="text-xs text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full">
                      {kanbanColumns[option.value]?.length ?? 0}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {isLoadingTasks && visibleTasks.length === 0 ? (
                      <div className="bg-background-50 border border-background-200 rounded-lg p-3 text-xs text-foreground-500">
                        Carregando tarefas...
                      </div>
                    ) : (kanbanColumns[option.value] ?? []).length === 0 ? (
                      <div className="bg-background-50 border border-background-200 rounded-lg p-3 text-xs text-foreground-500">
                        Nenhuma tarefa.
                      </div>
                    ) : (
                      (kanbanColumns[option.value] ?? []).map((task) => (
                        <div
                          key={task.id}
                          onClick={() => setSelectedTask(task)}
                          draggable
                          onDragStart={(event) => handleTaskDragStart(task, event)}
                          onDragEnd={handleTaskDragEnd}
                          className={`bg-background-50 border rounded-lg p-3 cursor-pointer hover:border-primary-200 transition-all ${
                            draggingTaskId === task.id ? 'opacity-60 border-primary-300' : 'border-background-200'
                          }`}
                        >
                          <p className="text-sm font-medium text-foreground-800">{task.title}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{task.clientName ?? '—'}</p>
                          <p className="text-[11px] text-foreground-400 mt-0.5">{compactText(task.caseTitle, task.leadName, task.legalProductName) || '—'}</p>
                          <div className="flex items-center justify-between mt-2">
                            <PriorityBadge priority={getTaskPriorityLabel(task.priority)} size="sm" />
                            <span className="text-xs text-foreground-400">{task.dueDate ?? '—'}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Drawer
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          title="Detalhe da Tarefa"
          footer={
            selectedTask ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditTask(selectedTask)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Editar
                </button>
                <button
                  onClick={() => void handleCompleteTask(selectedTask)}
                  disabled={selectedTask.status === 'completed' || isSavingTask || Boolean(completingTaskId) || Boolean(archivingTaskId)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-background-50 hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {selectedTask.status === 'completed' ? 'Concluída' : completingTaskId === selectedTask.id ? 'Concluindo...' : 'Concluir'}
                </button>
                <button
                  onClick={() => void handleArchiveTask(selectedTask)}
                  disabled={isSavingTask || Boolean(completingTaskId) || archivingTaskId === selectedTask.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {archivingTaskId === selectedTask.id ? 'Arquivando...' : 'Arquivar'}
                </button>
              </div>
            ) : undefined
          }
        >
          {selectedTask && (
            <div className="space-y-4">
              <h4 className="text-base font-semibold text-foreground-900">{selectedTask.title}</h4>
              <div className="flex items-center gap-2">
                <StatusBadge status={getTaskStatusLabel(selectedTask.status)} />
                <PriorityBadge priority={getTaskPriorityLabel(selectedTask.priority)} />
              </div>
              <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">{selectedTask.description ?? 'Sem descrição.'}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Cliente</p>
                  <p className="text-sm text-foreground-700">{selectedTask.clientName ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Caso</p>
                  <p className="text-sm text-foreground-700">{selectedTask.caseTitle ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Lead</p>
                  <p className="text-sm text-foreground-700">{selectedTask.leadName ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Produto Jurídico</p>
                  <p className="text-sm text-foreground-700">{selectedTask.legalProductName ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Responsável</p>
                  <p className="text-sm text-foreground-700">{selectedTask.responsibleUser ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Prazo</p>
                  <p className="text-sm font-semibold text-foreground-800">{selectedTask.dueDate ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Tipo</p>
                  <p className="text-sm text-foreground-700">{getTaskTypeLabel(selectedTask.taskType)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Status</p>
                  <p className="text-sm text-foreground-700">{getTaskStatusLabel(selectedTask.status)}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-1">Observações</p>
                <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">{selectedTask.notes ?? '—'}</p>
              </div>
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={showNewTask}
          onClose={closeNewTaskDrawer}
          title={editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}
          width="w-[520px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeNewTaskDrawer}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button onClick={handleSaveNewTask} className={saveButtonClassName}>
                {isSavingTask ? 'Salvando...' : editingTask ? 'Salvar Alterações' : 'Criar Tarefa'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
              <input
                type="text"
                value={newTaskForm.title}
                onChange={(e) => setNewTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Ex: Elaborar petição inicial..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={newTaskForm.description}
                onChange={(e) => setNewTaskForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                placeholder="Detalhes da tarefa..."
              ></textarea>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Responsável</label>
                <select
                  value={newTaskForm.responsibleUser}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, responsibleUser: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="Vinícius Meschick">Vinícius Meschick</option>
                  <option value="Dalila">Dalila</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Cliente</label>
                <select
                  value={newTaskForm.clientId}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, clientId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={clientsLoading}
                >
                  <option value="">{clientsLoading ? 'Carregando clientes...' : 'Selecione um cliente'}</option>
                  {clientOptions.map((client) => (
                    <option key={client.value} value={client.value}>
                      {client.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Caso</label>
                <select
                  value={newTaskForm.caseId}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, caseId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={isLoadingCases}
                >
                  <option value="">{isLoadingCases ? 'Carregando casos...' : 'Selecione um caso'}</option>
                  {caseOptions.map((caseItem) => (
                    <option key={caseItem.value} value={caseItem.value}>
                      {caseItem.label || 'Sem nome'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Lead</label>
                <select
                  value={newTaskForm.leadId}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, leadId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={isLoadingLeads}
                >
                  <option value="">{isLoadingLeads ? 'Carregando leads...' : 'Selecione um lead'}</option>
                  {leadOptions.map((lead) => (
                    <option key={lead.value} value={lead.value}>
                      {lead.label || 'Sem nome'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Produto Jurídico</label>
                <select
                  value={newTaskForm.legalProductId}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, legalProductId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={isLoadingLegalProducts}
                >
                  <option value="">{isLoadingLegalProducts ? 'Carregando produtos...' : 'Selecione um produto'}</option>
                  {legalProductOptions.map((product) => (
                    <option key={product.value} value={product.value}>
                      {product.label || 'Sem nome'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Prazo</label>
                <input
                  type="date"
                  value={newTaskForm.dueDate}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tipo</label>
                <select
                  value={newTaskForm.taskType}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, taskType: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {taskTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Prioridade</label>
                <select
                  value={newTaskForm.priority}
                  onChange={(e) => setNewTaskForm((prev) => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {taskPriorityFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={newTaskForm.status}
                  onChange={(e) =>
                    setNewTaskForm((prev) => ({
                      ...prev,
                      status: e.target.value,
                      completedAt: e.target.value === 'completed' ? prev.completedAt : '',
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {taskStatusFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={newTaskForm.notes}
                onChange={(e) => setNewTaskForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              ></textarea>
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default TasksPage;
